import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { findLast } from 'lodash';
import { InputBaseModel } from '../../models';
import { InputRenderer, AddDeductionButton } from '../../components';
import { NodeProps } from '../../componentTypes';
import { LayoutRenderer } from '../../layout_renderers';
import { InputIds, NodeIds } from '../../misc';
import { Utils } from '../../../custom_shared/misc';
import { useNodeForm } from '../../hooks';
import { ActionParams } from '../../types';
import { GetComponentRowsFunction } from '../../componentTypes';
import { InputComponentRegistry } from '../../node_inputs';

const addDeductionButtonKey = 'add-deduction-button';

const InputComponents = InputComponentRegistry[NodeIds.Deductions];

const isDeductionInput = (input: InputBaseModel) => {
    return (
        input.id.startsWith(InputIds.Deductions.DeductionType) ||
        input.id.startsWith(InputIds.Deductions.DeductionValue)
    );
};

const DeductionsNodeRenderer: React.FC<NodeProps> = ({ node }: NodeProps) => {
    const { form, formKey, onValuesChange } = useNodeForm(node);

    const getComponentRows: GetComponentRowsFunction = React.useCallback(
        (inputs: InputBaseModel[], actionParams: ActionParams) => {
            const { basicInputs, deductionInputs } = inputs.reduce<{
                basicInputs: InputBaseModel[];
                deductionInputs: InputBaseModel[];
            }>(
                (acc, input) => {
                    if (isDeductionInput(input)) {
                        acc.deductionInputs.push(input);
                    } else {
                        acc.basicInputs.push(input);
                    }

                    return acc;
                },
                { basicInputs: [], deductionInputs: [] }
            );

            const basicInputRows = Utils.groupArray(basicInputs, 3).map(inputGroup =>
                inputGroup.map(input => ({
                    component: (
                        <InputRenderer
                            key={input.id}
                            input={input}
                            component={InputComponents[input.id as keyof typeof InputComponents]}
                        />
                    )
                }))
            );

            const deductionInputRows = Utils.groupArray(deductionInputs, 2).map(inputGroup =>
                inputGroup.map(input => ({
                    component: (
                        <InputRenderer
                            key={input.id}
                            input={input}
                            component={
                                input.id.startsWith(InputIds.Deductions.DeductionType)
                                    ? InputComponents[InputIds.Deductions.DeductionType]
                                    : InputComponents[InputIds.Deductions.DeductionValue]
                            }
                        />
                    )
                }))
            );

            const rows = [...basicInputRows, ...deductionInputRows];

            const lastRow = findLast(rows, row => row.length < 3);

            const addDeductionButton = {
                component: <AddDeductionButton key={addDeductionButtonKey} actionParams={actionParams} />
            };

            if (lastRow) {
                lastRow.push(addDeductionButton);
            } else {
                rows.push([addDeductionButton]);
            }

            return rows;
        },
        []
    );

    return (
        <LayoutRenderer
            node={node}
            form={form}
            formKey={formKey}
            onValuesChange={onValuesChange}
            getComponentRows={getComponentRows}
        />
    );
};

export default observer(DeductionsNodeRenderer);
